import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Container, Row, Col, CardDeck } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import SceneCard from './scene-card';
import SpinnerLoad from '../common/spinner';
import constants from '../../const';
import { connect } from 'react-redux';

const PageContainer = styled.div`
    background: linear-gradient(135deg, #f0f9fc 0%, #f3f4f6 100%);
    min-height: 100vh;
    padding: 30px 0;
`;

const HeaderSection = styled.div`
    background: transparent;
    padding: 10px 0 20px;
    margin-bottom: 0;

    h1 {
        font-size: 42px;
        font-weight: 700;
        color: #333;
        margin: 0 0 8px;
    }
`;

const MovieMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 20px;
`;

const MetaBadge = styled.div`
    display: inline-flex;
    padding: 6px 14px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 13px;
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(15, 118, 110, 0.25);
`;

const DescriptionBox = styled.div`
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 30px;
    line-height: 1.8;
    color: #555;
    font-size: 15px;
    border-left: 4px solid #06b6d4;
    white-space: pre-wrap;
`;

const SectionTitle = styled.h3`
    color: #333;
    font-weight: 700;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
        content: '';
        width: 4px;
        height: 24px;
        background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
        border-radius: 2px;
    }
`;

const PlayAllButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 14px 28px;
    border: none;
    border-radius: 12px;
    font-weight: 700;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(15, 118, 110, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 24px rgba(15, 118, 110, 0.4);
    }

    &:active {
        transform: translateY(0);
    }
`;

const PlayerContainer = styled.div`
    background: #000;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
    margin-bottom: 20px;
`;

const PlayerControls = styled.div`
    background: white;
    border-radius: 12px;
    padding: 16px 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 30px;
`;

const SceneList = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 8px 0;

    &::-webkit-scrollbar {
        height: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
        border-radius: 3px;
    }
`;

const SceneListItem = styled.button`
    flex-shrink: 0;
    padding: 10px 16px;
    border: 2px solid ${props => props.active ? '#0f766e' : '#e0e0e0'};
    border-radius: 8px;
    background: ${props => props.active ? 'linear-gradient(135deg, #0f766e 0%, #06b6d4 100%)' : 'white'};
    color: ${props => props.active ? 'white' : '#333'};
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: #0f766e;
        transform: translateY(-1px);
    }
`;

const NowPlaying = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 14px;
    color: #555;

    strong {
        color: #0f766e;
        font-size: 16px;
    }
`;

const CloseButton = styled.button`
    padding: 8px 20px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    background: white;
    color: #666;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #e63946;
        color: #e63946;
    }
`;

const ScenesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: flex-start;
`;

const OrderControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
`;

const OrderButton = styled.button`
    padding: 8px 20px;
    border: 2px solid ${props => props.active ? '#0f766e' : '#e0e0e0'};
    border-radius: 8px;
    background: ${props => props.active ? 'linear-gradient(135deg, #0f766e 0%, #06b6d4 100%)' : 'white'};
    color: ${props => props.active ? 'white' : '#666'};
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #0f766e;
    }
`;

const SaveOrderButton = styled.button`
    padding: 8px 20px;
    border: none;
    border-radius: 8px;
    background: #22c55e;
    color: white;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #16a34a;
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

const OrderBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
`;

const ArrowBtn = styled.button`
    width: 28px;
    height: 28px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    color: #333;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background: #0f766e;
        color: white;
        border-color: #0f766e;
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
        &:hover { background: white; color: #333; border-color: #e0e0e0; }
    }
`;

const OrderNumber = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    color: white;
    font-weight: 700;
    font-size: 13px;
`;

function MovieView(props) {
    const [movie, setMovie] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [playing, setPlaying] = useState(false);
    const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
    const [editingOrder, setEditingOrder] = useState(false);
    const [saving, setSaving] = useState(false);
    const [posterKey, setPosterKey] = useState(0);
    const [uploading, setUploading] = useState(false);
    const videoRef = useRef(null);
    const posterInputRef = useRef(null);

    var token = `?token=${localStorage.getItem('token').slice(7)}`;
    const movieId = props.match.params.id;

    useEffect(() => {
        setLoading(true);
        axios.get(`${constants.DATA1_API_URL}/scenes/movie/${movieId}`, {
            headers: { 'Authorization': `${localStorage.getItem('token')}` }
        })
        .then(res => {
            setMovie(res.data.movie);
            setScenes(res.data.scenes);
            setSite(res.data.site);
            setLoading(false);
        })
        .catch(() => setLoading(false));
    }, [movieId]);

    const getVideoUrl = useCallback((scene) => {
        const sceneData = typeof scene.scene === 'string' ? JSON.parse(scene.scene) : scene.scene;
        return `${constants.CONTENT_DOMAIN}${sceneData.videoFull}${token}`;
    }, [token]);

    const getPosterUrl = useCallback((scene) => {
        const sceneData = typeof scene.scene === 'string' ? JSON.parse(scene.scene) : scene.scene;
        return `${constants.TRAILER_CDN}${sceneData.posterURL}`;
    }, []);

    const getSceneTitle = useCallback((scene) => {
        const sceneData = typeof scene.scene === 'string' ? JSON.parse(scene.scene) : scene.scene;
        return sceneData.title;
    }, []);

    const getSceneDuration = useCallback((scene) => {
        const sceneData = typeof scene.scene === 'string' ? JSON.parse(scene.scene) : scene.scene;
        return sceneData.duration;
    }, []);

    const getSceneId = useCallback((scene) => {
        const sceneData = typeof scene.scene === 'string' ? JSON.parse(scene.scene) : scene.scene;
        return sceneData.id;
    }, []);

    const moveScene = (fromIdx, toIdx) => {
        const updated = [...scenes];
        const [moved] = updated.splice(fromIdx, 1);
        updated.splice(toIdx, 0, moved);
        setScenes(updated);
    };

    const saveOrder = () => {
        setSaving(true);
        const sceneOrders = scenes.map((scene, idx) => ({
            sceneId: getSceneId(scene),
            sortOrder: idx + 1
        }));
        axios.put(`${constants.DATA1_API_URL}/scenes/movie/${movieId}/sort`,
            { sceneOrders },
            { headers: { 'Authorization': `${localStorage.getItem('token')}` } }
        )
        .then(() => { setSaving(false); setEditingOrder(false); })
        .catch(() => setSaving(false));
    };

    const handlePosterUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('poster', file);
        const uploadToken = localStorage.getItem('token').slice(7);
        axios.post(`${constants.CONTENT_DOMAIN}upload/movie/${movieId}/poster?token=${uploadToken}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(() => { setUploading(false); setPosterKey(prev => prev + 1); })
        .catch(() => setUploading(false));
    };

    const startPlaying = (index = 0) => {
        setCurrentSceneIndex(index);
        setPlaying(true);
    };

    const stopPlaying = () => {
        setPlaying(false);
        setCurrentSceneIndex(0);
    };

    const handleVideoEnded = useCallback(() => {
        if (currentSceneIndex < scenes.length - 1) {
            setCurrentSceneIndex(prev => prev + 1);
        } else {
            setPlaying(false);
        }
    }, [currentSceneIndex, scenes.length]);

    const jumpToScene = (index) => {
        setCurrentSceneIndex(index);
    };

    useEffect(() => {
        if (playing && videoRef.current) {
            videoRef.current.load();
            videoRef.current.play().catch(() => {});
        }
    }, [currentSceneIndex, playing]);

    const totalDuration = scenes.reduce((sum, s) => sum + (getSceneDuration(s) || 0), 0);

    if (loading) return <SpinnerLoad />;
    if (!movie) return <Container><h2>Movie not found</h2></Container>;

    document.title = `${movie.title} | Movie`;

    return (
        <PageContainer>
            <Container style={{ maxWidth: '1400px' }}>
                <HeaderSection>
                    <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative', width: '180px', flexShrink: 0 }}>
                            <img
                                key={posterKey}
                                src={`${constants.CONTENT_DOMAIN}download/sus-media/movies/${movieId}/poster.webp${token}`}
                                alt={movie.title}
                                style={{ width: '180px', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', aspectRatio: '2/3', objectFit: 'cover' }}
                                onError={(e) => {
                                    if (scenes.length > 0) {
                                        const sd = typeof scenes[0].scene === 'string' ? JSON.parse(scenes[0].scene) : scenes[0].scene;
                                        e.target.src = `${constants.TRAILER_CDN}${sd.posterURL}`;
                                    }
                                    e.target.style.aspectRatio = '16/9';
                                }}
                            />
                            {props.perms.some(e => e.role.id === 2) && (
                                <>
                                    <input ref={posterInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePosterUpload} />
                                    <button
                                        onClick={() => posterInputRef.current.click()}
                                        disabled={uploading}
                                        style={{ position: 'absolute', bottom: '8px', right: '8px', padding: '4px 10px', borderRadius: '6px', border: 'none', background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        {uploading ? '...' : '📷'}
                                    </button>
                                </>
                            )}
                        </div>
                        <div>
                            <h1>{movie.title}</h1>
                            <MovieMeta>
                                {site && <MetaBadge>{site.name}</MetaBadge>}
                                <MetaBadge>{scenes.length} Scenes</MetaBadge>
                                {totalDuration > 0 && (
                                    <MetaBadge>
                                        {Math.floor(totalDuration)}m {Math.round((totalDuration % 1) * 60)}s total
                                    </MetaBadge>
                                )}
                                {movie.releaseDate && (
                                    <MetaBadge>{dayjs(movie.releaseDate).format('D-MMM-YYYY')}</MetaBadge>
                                )}
                            </MovieMeta>
                            <PlayAllButton onClick={() => startPlaying(0)}>
                                ▶ Play Movie
                            </PlayAllButton>
                        </div>
                    </div>
                </HeaderSection>

                {movie.description && (
                    <DescriptionBox>{movie.description}</DescriptionBox>
                )}

                {playing && scenes.length > 0 && (
                    <>
                        <PlayerContainer>
                            <video
                                ref={videoRef}
                                controlsList="nodownload"
                                playsInline
                                controls
                                autoPlay
                                style={{ width: '100%', display: 'block' }}
                                poster={getPosterUrl(scenes[currentSceneIndex])}
                                onEnded={handleVideoEnded}
                            >
                                <source src={getVideoUrl(scenes[currentSceneIndex])} />
                            </video>
                        </PlayerContainer>
                        <PlayerControls>
                            <NowPlaying>
                                <div>
                                    Now Playing: <strong>Scene {currentSceneIndex + 1}/{scenes.length}</strong>
                                    {' — '}{getSceneTitle(scenes[currentSceneIndex])}
                                </div>
                                <CloseButton onClick={stopPlaying}>Close Player</CloseButton>
                            </NowPlaying>
                            <SceneList>
                                {scenes.map((scene, idx) => (
                                    <SceneListItem
                                        key={idx}
                                        active={idx === currentSceneIndex}
                                        onClick={() => jumpToScene(idx)}
                                    >
                                        {idx + 1}. {getSceneTitle(scene)}
                                    </SceneListItem>
                                ))}
                            </SceneList>
                        </PlayerControls>
                    </>
                )}

                <SectionTitle>All Scenes</SectionTitle>
                {props.perms.some(e => e.role.id === 2) && (
                    <OrderControls>
                        <OrderButton active={editingOrder} onClick={() => setEditingOrder(!editingOrder)}>
                            {editingOrder ? 'Cancel' : 'Edit Order'}
                        </OrderButton>
                        {editingOrder && (
                            <SaveOrderButton onClick={saveOrder} disabled={saving}>
                                {saving ? 'Saving...' : 'Save Order'}
                            </SaveOrderButton>
                        )}
                    </OrderControls>
                )}
                <ScenesGrid>
                    {scenes.map((scene, idx) => {
                        const sceneData = typeof scene.scene === 'string' ? JSON.parse(scene.scene) : scene.scene;
                        const actors = typeof scene.actors === 'string' ? JSON.parse(scene.actors) : scene.actors;
                        const siteData = typeof scene.site === 'string' ? JSON.parse(scene.site) : scene.site;
                        const movieData = typeof scene.movie === 'string' ? JSON.parse(scene.movie) : scene.movie;
                        const aliases = typeof scene.aliases === 'string' ? JSON.parse(scene.aliases) : scene.aliases;

                        const formattedScene = {
                            scene: sceneData,
                            actors: actors,
                            site: siteData,
                            movie: movieData,
                            aliases: aliases,
                            duration: scene.duration,
                            positionIds: scene.positionIds
                        };

                        return (
                            <div key={sceneData.id} style={{ position: 'relative' }}>
                                {editingOrder && (
                                    <OrderBadge>
                                        <ArrowBtn disabled={idx === 0} onClick={() => moveScene(idx, idx - 1)}>↑</ArrowBtn>
                                        <OrderNumber>{idx + 1}</OrderNumber>
                                        <ArrowBtn disabled={idx === scenes.length - 1} onClick={() => moveScene(idx, idx + 1)}>↓</ArrowBtn>
                                    </OrderBadge>
                                )}
                                <CardDeck>
                                    <SceneCard scene={formattedScene} roles={props.perms} />
                                </CardDeck>
                                {playing ? null : (
                                    <PlaySceneButton onClick={() => startPlaying(idx)}>
                                        ▶ Play from here
                                    </PlaySceneButton>
                                )}
                            </div>
                        );
                    })}
                </ScenesGrid>
            </Container>
        </PageContainer>
    );
}

const PlaySceneButton = styled.button`
    display: block;
    width: 100%;
    padding: 6px;
    margin-top: 4px;
    border: none;
    border-radius: 6px;
    background: rgba(15, 118, 110, 0.1);
    color: #0f766e;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
        color: white;
    }
`;

const mapStateToProps = (state) => {
    return {
        perms: state.users.permissions.perms
    };
};

export default connect(mapStateToProps)(MovieView);
